import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { attendanceQueryKeys } from '@/features/attendance/api/attendance.api';
import { leaveApplicationQueryKeys } from '@/features/leaveApplications/api/leaveApplications.api';
import { leaveReportQueryKeys } from '@/features/leaveReport/api/leaveReport.api';
import { showApiErrorToast } from '@/utils/errors';
import { createHoliday, deleteHoliday, holidayQueryKeys, updateHoliday } from '../api/holidays.api';
import type { HolidayPayload } from '../types/holiday.types';

interface SaveHolidayArgs {
  id: number | null;
  payload: HolidayPayload;
}

const HOLIDAY_AFFECTED_QUERY_KEYS = [
  holidayQueryKeys.all(),
  attendanceQueryKeys.all(),
  leaveReportQueryKeys.all(),
  leaveApplicationQueryKeys.all(),
];

function useRefreshHolidayEffects() {
  const queryClient = useQueryClient();
  return () => {
    for (const queryKey of HOLIDAY_AFFECTED_QUERY_KEYS) {
      void queryClient.invalidateQueries({ queryKey });
    }
  };
}

export function useSaveHoliday(onSaved: () => void) {
  const refreshHolidayEffects = useRefreshHolidayEffects();

  return useMutation({
    mutationFn: ({ id, payload }: SaveHolidayArgs) =>
      id === null ? createHoliday(payload) : updateHoliday(id, payload),
    onSuccess: (_, { id }) => {
      refreshHolidayEffects();
      toast.success(id === null ? 'Holiday added' : 'Holiday updated');
      onSaved();
    },
    onError: (error, { id }) => {
      showApiErrorToast(
        error,
        id === null ? 'Could not add the holiday' : 'Could not update the holiday',
      );
    },
  });
}

export function useDeleteHoliday(onDeleted: () => void) {
  const refreshHolidayEffects = useRefreshHolidayEffects();

  return useMutation({
    mutationFn: (id: number) => deleteHoliday(id),
    onSuccess: () => {
      refreshHolidayEffects();
      toast.success('Holiday deleted');
      onDeleted();
    },
    onError: (error) => {
      showApiErrorToast(error, 'Could not delete the holiday');
    },
  });
}
